import { Event } from 'services/api';
import { GetProfileResponse } from 'services/api/apis/profile/dto/get-profile.response';

export type TodayCardState = 'now' | 'upcoming_today';

const localDayKey = (date: Date): string => {
  const pad = (value: number): string => `${value}`.padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
};

const addDaysKey = (dayKey: string, days: number): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!match) return dayKey;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  date.setDate(date.getDate() + days);
  return localDayKey(date);
};

/** Event dates are calendar days. Build a local Date so US timezones don't shift the day. */
export const eventDateTime = (
  isoDate?: string,
  time?: string,
): Date | null => {
  if (!isoDate) return null;
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!day) return null;
  let hours = 0;
  let minutes = 0;
  if (time) {
    const match = /^(\d{1,2}):(\d{2})/.exec(time.trim());
    if (match) {
      hours = Number(match[1]);
      minutes = Number(match[2]);
      const meridiem = /\b(AM|PM)\b/i.exec(time)?.[1]?.toUpperCase();
      if (meridiem === 'PM' && hours < 12) hours += 12;
      if (meridiem === 'AM' && hours === 12) hours = 0;
    }
  }
  return new Date(
    Number(day[1]),
    Number(day[2]) - 1,
    Number(day[3]),
    hours,
    minutes,
    0,
    0,
  );
};

const eventEnd = (event: Event): Date | null => {
  const start = eventDateTime(event.date, event.startTime);
  const end = eventDateTime(event.date, event.endTime);
  if (!start) return end;
  if (!end) {
    const close = new Date(start);
    close.setHours(23, 59, 59, 0);
    return close;
  }
  if (end.getTime() <= start.getTime())
    return new Date(end.getTime() + 24 * 60 * 60 * 1000);

  return end;
};

export const isLiveNow = (event: Event, now: Date = new Date()): boolean => {
  if (event.status === 'CANCELLED' || event.status === 'FINISHED') return false;
  const start = eventDateTime(event.date, event.startTime);
  const end = eventEnd(event);
  if (!start || !end) return false;
  const nowMs = now.getTime();
  return nowMs >= start.getTime() && nowMs <= end.getTime();
};

const hasEnded = (event: Event, now: Date): boolean => {
  const end = eventEnd(event);
  if (!end) return false;
  return end.getTime() < now.getTime();
};

const startMs = (event: Event): number =>
  eventDateTime(event.date, event.startTime)?.getTime() ?? 0;

export const partitionFeedEvents = (
  events: Event[],
  now: Date = new Date(),
): { today: Event[]; upcoming: Event[] } => {
  const todayKey = localDayKey(now);
  const untilKey = addDaysKey(todayKey, 7);
  const today: Event[] = [];
  const upcoming: Event[] = [];

  for (const event of events) {
    if (!event.date) continue;
    if (
      event.status === 'CANCELLED' ||
      event.status === 'FINISHED' ||
      event.status === 'EXPIRED'
    )
      continue;

    const day = event.date.slice(0, 10);
    if (day === todayKey) {
      if (!hasEnded(event, now)) today.push(event);
    } else if (day > todayKey && day <= untilKey) {
      upcoming.push(event);
    }
  }

  today.sort((a, b) => {
    const aLive = isLiveNow(a, now) ? 0 : 1;
    const bLive = isLiveNow(b, now) ? 0 : 1;
    if (aLive !== bLive) return aLive - bLive;
    return startMs(a) - startMs(b);
  });

  upcoming.sort((a, b) => {
    const day = a.date.slice(0, 10).localeCompare(b.date.slice(0, 10));
    if (day !== 0) return day;
    return startMs(a) - startMs(b);
  });

  return { today, upcoming };
};

export const todayCardState = (
  event: Event,
  now: Date = new Date(),
): TodayCardState => (isLiveNow(event, now) ? 'now' : 'upcoming_today');

const sameSchool = (event: Event, profile: GetProfileResponse): boolean => {
  const eventSchoolId = event.school?.id;
  const profileSchoolId = profile.school?.id;
  return Boolean(
    eventSchoolId && profileSchoolId && eventSchoolId === profileSchoolId,
  );
};

/**
 * Who may check attendees in from a feed card.
 * Mirrors `canOrganizeEventCheckIn` in services/api: masters organize every
 * school, executive admins only organize events at their own school, and
 * org leaders/admins only organize events their organization hosts.
 */
export const canOrganizeFeedEvent = (
  event: Event,
  profile?: GetProfileResponse | null,
): boolean => {
  if (!profile) return false;
  const roles = profile.role ?? [];
  if (roles.includes('master')) return true;
  if (roles.includes('executive-admin') && sameSchool(event, profile)) {
    return true;
  }
  const hostIds = new Set((event.hostOrganizations ?? []).map(org => org.id));
  return (
    (profile.ledOrganizations ?? []).some(org => hostIds.has(org.id)) ||
    (profile.adminOrganizations ?? []).some(org => hostIds.has(org.id))
  );
};
